import { Box, Flex, NumberInput, NumberInputField, Text } from '@elkaso-app/web-design';
import type { MouseEvent } from 'react';
import { useRef, useState } from 'react';
import type { PriceTier, UomLadder } from 'utils/price-units';
import { availableTiers, derivePrices, formatMoney } from 'utils/price-units';

export interface RowPriceCellProps {
  price: number | null;
  tier: PriceTier | null;
  ladder: UomLadder;
  /** The unit word the supplier's file used, shown so the source stays visible. */
  sourceWord: string | null;
  /** Not 1 when the matcher applied a dimensional conversion (per-100g → per-kg). */
  scale: number;
  readOnly: boolean;
  onCommit: (next: { price: number; tier: PriceTier | null }) => void;
}

/**
 * Inline price + rung chips for one import row.
 *
 * Reproduces the interaction of prices/components/price-cell.tsx rather than
 * reusing it, and that is deliberate: PriceCell is wired to
 * useUpdateExclusiveDealApi / useCreateDealFromMasterApi and writes straight to
 * supplier_prices. Here nothing may touch a real price until the run is
 * committed — this only PATCHes the staged import row.
 *
 * The two non-obvious behaviours carried over verbatim, because both are bugs
 * if you skip them:
 *
 *  - isCancelledRef: Escape unmounts the input, which still fires its blur
 *    handler, and that blur would commit the value the operator just cancelled.
 *  - onMouseDown preventDefault on the chips: without it, clicking a rung blurs
 *    the input first and commits mid-edit against the OLD rung.
 */
export const RowPriceCell = ({ price, tier, ladder, sourceWord, scale, readOnly, onCommit }: RowPriceCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [draftTier, setDraftTier] = useState<PriceTier | null>(null);
  const isCancelledRef = useRef(false);

  const tierOptions = availableTiers(ladder);

  const startEdit = () => {
    if (readOnly) return;
    isCancelledRef.current = false;
    setValue(price != null ? String(price) : '');
    // Seeded from the row's RESOLVED rung. Never from a fallback: an
    // unresolved rung must stay unresolved until a human picks one.
    setDraftTier(tier);
    setIsEditing(true);
  };

  const cancel = () => {
    isCancelledRef.current = true;
    setIsEditing(false);
  };

  const commit = () => {
    setIsEditing(false);

    const next = Number(value);
    if (!value.trim() || !Number.isFinite(next) || next <= 0) return;
    if (next === price && draftTier === tier) return;

    onCommit({ price: next, tier: draftTier });
  };

  if (isEditing) {
    const typed = Number(value);
    const derived = draftTier && Number.isFinite(typed) && typed > 0 ? derivePrices(typed, draftTier, ladder) : [];

    return (
      <Box px='6px' py='4px' boxShadow='inset 0 0 0 2px var(--chakra-colors-red-500)' borderRadius='2px' bg='white'>
        <NumberInput size='sm' min={0} value={value} onChange={(valueAsString) => setValue(valueAsString)}>
          <NumberInputField
            autoFocus
            textAlign='end'
            fontWeight='bold'
            border='none'
            px='4px'
            onFocus={(e) => e.currentTarget.select()}
            onBlur={() => {
              if (isCancelledRef.current) return;
              commit();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') cancel();
            }}
          />
        </NumberInput>

        {tierOptions.length > 1 && (
          <Flex justify='flex-end' gap='3px' mt='3px' wrap='wrap'>
            {tierOptions.map((option) => (
              <Text
                key={option.tier}
                as='button'
                type='button'
                onMouseDown={(e: MouseEvent) => {
                  e.preventDefault();
                  setDraftTier(option.tier);
                }}
                fontSize='10px'
                fontWeight={draftTier === option.tier ? 'bold' : 'medium'}
                color={draftTier === option.tier ? 'white' : 'gray.600'}
                bg={draftTier === option.tier ? 'red.500' : 'gray.100'}
                px='6px'
                py='1px'
                borderRadius='4px'
                cursor='pointer'>
                / {option.unit}
              </Text>
            ))}
          </Flex>
        )}

        {derived.length > 0 && (
          <Box textAlign='end' mt='2px'>
            {derived.map((line) => (
              <Text key={line.tier} fontSize='10.5px' color='gray.500' lineHeight='1.5' whiteSpace='nowrap'>
                ≈ {formatMoney(line.amount)} / {line.unit}
              </Text>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      onClick={startEdit}
      cursor={readOnly ? 'default' : 'text'}
      px='6px'
      py='4px'
      borderRadius='2px'
      _hover={readOnly ? undefined : { bg: 'gray.100' }}>
      <Text fontSize='sm' whiteSpace='nowrap' textAlign='end'>
        {price != null ? formatMoney(price) : '—'}
        {tier && (
          <Text as='span' fontSize='xs' color='gray.500'>
            {' '}
            / {tier.toLowerCase()}
          </Text>
        )}
      </Text>
      {sourceWord && (
        <Text fontSize='10.5px' color='gray.500' textAlign='end' whiteSpace='nowrap'>
          file: “{sourceWord}”{scale !== 1 ? ` ×${scale}` : ''}
        </Text>
      )}
    </Box>
  );
};
